import { Fragment, useState, useEffect, useId, useRef, useLayoutEffect, lazy, Suspense, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getActiveUser, setActiveUser } from '../lib/api';
import { loadAuthConfig, signOut } from '../lib/auth';
import { toast } from '../lib/toast';
import { Toaster, Skeleton } from './ui';
import { ImageLightbox } from './ImageLightbox';
import { Select } from './Select';
import {
  useNotifications,
  useNotificationCounts,
  useNotificationActions,
  notifHref,
  type Notif,
  type CategoryFilter,
} from '../features/notifications/api';
import { DROPDOWN_TABS, NotificationItem, EMPTY_COPY } from '../features/notifications/ui';
import { MorningBriefWidget } from '../features/morning-brief/widget';
import { GlobalSearch } from '../features/search/GlobalSearch';
import { MyCourtBadge } from '../features/balls/TicketBall';
import { Icon } from '../lib/icons';
import { APP_VERSION } from '../lib/version';
import { UserAvatar } from './UserAvatar';
import { WorkspaceSwitcher } from '../features/workspaces/WorkspaceSwitcher';
import { AtlasBrand, TracxBrand } from './Brand';
import { useAppFeatures, FeatureGate } from '../features/app-features';
import { featureForPath } from '@atlas/contracts';

/** "super_admin" -> "Super Admin" for display. */
const roleLabel = (r?: string | null) => (r ? r.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '');

// Lazy so the rich-text editor (TipTap) stays out of the initial bundle.
const CreateTicketModal = lazy(() => import('./CreateTicketModal').then((m) => ({ default: m.CreateTicketModal })));

// Consolidated, grouped navigation (#11): direct links + dropdown groups, so
// the long flat bar becomes a handful of menus. "My Tickets" (formerly the
// top-level My Work) lives under the Tickets group (formerly Work); the user's
// saved ticket views are appended to that group at render time.
// `roles` gates VISIBILITY only — the API is the real authority. No entry uses
// it today: Releases used to be PO-tier-only, but everyone needs to see what is
// shipping, so the page is open to all and its deploy/rollback controls are
// hidden for non-managers instead (the API still gates them).
type NavItem = { label: string; path: string; shortcut?: string; end?: boolean; wip?: boolean; roles?: string[]; hideIf?: (ctx: { showRoadmap: boolean }) => boolean; heading?: string };
type NavGroupDef = { label: string; items: NavItem[] };
type NavEntry = NavItem | NavGroupDef;
const isGroup = (e: NavEntry): e is NavGroupDef => 'items' in e;

const NAV_GROUPS: NavEntry[] = [
  { label: 'Dashboard', path: '/', shortcut: 'd', end: true },
  { label: 'Tickets', items: [
    { label: 'My Tickets', path: '/my-work', shortcut: 'w' },
    { label: 'Team court', path: '/team-court' },
    { label: 'All Tickets', path: '/tickets', shortcut: 't' },
    { label: 'Morning Brief', path: '/morning-brief', shortcut: 'm', wip: true },
    { label: 'Transcript', path: '/transcript-wizard' },
  ] },
  { label: 'Planning', items: [
    { label: 'Backlog', path: '/backlog', shortcut: 'k' },
    { label: 'Sprints', path: '/sprints', shortcut: 's' },
    { label: 'Releases', path: '/releases' },
    { label: 'Roadmap', path: '/roadmap', shortcut: 'r' },
    { label: 'Prioritize', path: '/prioritize', shortcut: 'p' },
  ] },
  { label: 'Docs', path: '/docs' },
  { label: 'Onboarding', path: '/customers', shortcut: 'o' },
  { label: 'R&D Ops', items: [
    { label: 'Risk', path: '/third-party-risk', shortcut: 'x' },
    { label: 'Infrastructure', path: '/infrastructure', shortcut: 'i' },
    { label: 'Development agent', path: '/development-agent', roles: ['admin','super_admin'] },
    { label: 'Admin', path: '/admin' },
  ] },
];

/** A nav path is "active" for exact roots (/) or path-prefix routes (/board/:kind, /docs/*). */
const pathActive = (pathname: string, path: string, end?: boolean) =>
  end ? pathname === path : pathname === path || pathname.startsWith(path + '/');

/** Keyboard `g`-shortcuts, derived from NAV_GROUPS so they never drift. */
const GO_MAP: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const e of NAV_GROUPS) for (const i of (isGroup(e) ? e.items : [e])) if (i.shortcut && !i.wip) m[i.shortcut] = i.path;
  return m;
})();

const PERSONAS = [
  'admin@tracx.local', 'lead@tracx.local', 'dev@tracx.local',
  'qa@tracx.local', 'support@tracx.local', 'cx@tracx.local',
];

// Roadmap is a WIP nav tab (#5): default-on only for this owner; admins grant it
// per user via Admin → Users. Keep in sync with the API's resolveShowRoadmap.
const ROADMAP_DEFAULT_EMAIL = 'jay@tracxsystems.com';
function parsePrefs(raw?: string | null): Record<string, unknown> {
  try { return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}

export function AppShell({ children }: { children: ReactNode }) {
  const features = useAppFeatures();
  const available = (path: string) => { const key = featureForPath(path); return !key || features.data?.flags[key] === true; };
  const nav = useNavigate();
  const qc = useQueryClient();
  const { pathname, search } = useLocation();
  // The Docs workspace gets its own darker canvas background (tree + document +
  // metadata rails). This only drives the background — page WIDTH is full-bleed
  // application-wide (see <main> below); a page that wants a narrower reading
  // column constrains its own content rather than relying on the shell.
  const wideCanvas = pathname.startsWith('/docs');
  const [createOpen, setCreateOpen] = useState(false);
  const [user, setUser] = useState(getActiveUser());

  // Auth mode drives the header control: dev persona switcher vs signed-in user.
  const { data: authCfg } = useQuery({ queryKey: ['auth', 'config'], queryFn: loadAuthConfig, staleTime: Infinity });
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<{ user: { id: string; email: string; name: string; role: string; preferences?: string | null } }>('/v2/auth/me'),
    retry: false,
  });
  const devMode = (authCfg?.mode ?? 'dev') === 'dev';

  // Per-user Roadmap tab visibility (#5): explicit pref wins, else default-on
  // only for the owner. The route still exists when hidden — this is a nav
  // declutter, not access control.
  const roadmapPref = parsePrefs(me?.user.preferences).showRoadmap;
  const showRoadmap = typeof roadmapPref === 'boolean' ? roadmapPref : me?.user.email === ROADMAP_DEFAULT_EMAIL;

  // Drives `roles` on nav items. In dev-persona mode /auth/me still resolves the
  // switched persona, so this follows the persona switcher too.
  const role = me?.user.role ?? '';

  // The user's saved ticket views become menu items under the Tickets group.
  // Same query key as the Tickets page's SavedViewBar, so saving/deleting a
  // view there updates this menu immediately.
  const savedViews = useQuery({
    queryKey: ['views', 'tickets'],
    queryFn: () => api.get<{ items: { id: string; name: string; mine: boolean; visibility: string }[] }>('/v2/views?viewType=tickets'),
    retry: false,
  });
  // Two sections: MY VIEWS (my own private views) and COMPANY VIEWS (anything
  // saved "Company-wide", by anyone — so they show for everyone).
  const allViews = savedViews.data?.items ?? [];
  const myViewItems: NavItem[] = [
    ...allViews
      .filter((v) => v.mine && v.visibility === 'private')
      .map((v, i) => ({ label: v.name, path: `/tickets?view=${v.id}`, heading: i === 0 ? 'My views' : undefined })),
    ...allViews
      .filter((v) => v.visibility !== 'private')
      .map((v, i) => ({ label: v.name, path: `/tickets?view=${v.id}`, heading: i === 0 ? 'Company views' : undefined })),
  ];

  const toggleTheme = () => {
    const dark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('atlas.theme', dark ? 'dark' : 'light');
  };

  // Live updates: refresh affected queries as domain events stream in (SSE).
  useEffect(() => {
    const es = new EventSource('/api/v2/events/stream');
    es.onmessage = () => {
      for (const key of [
        'tickets', 'dashboard', 'board', 'backlog', 'notifications', 'ticket', 'report',
        'report-run', 'reports', 'dashboards', 'saved-filters', 'docs', 'morning-brief',
        // Advanced Work Views families
        'tags', 'views', 'boards', 'board-tickets', 'backlog-board', 'roadmap', 'feature-requests',
        'risk', 'infra', 'balls', 'ball-recipients',
      ]) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    };
    return () => es.close();
  }, [qc]);

  // Global keyboard shortcuts: `c` opens Create; `g` then a key jumps to a
  // section (g→t Tickets, k Backlog, r Roadmap, d Dashboard, w My Tickets).
  useEffect(() => {
    let goto = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const GO = GO_MAP;
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[data-atlas-modal]') || el?.closest('[aria-haspopup], [data-atlas-popover]')) return;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      if (goto) {
        goto = false;
        if (GO[e.key]) { e.preventDefault(); nav(GO[e.key]); }
        return;
      }
      if (e.key === 'g') { goto = true; clearTimeout(timer); timer = setTimeout(() => { goto = false; }, 800); }
      else if (e.key === 'c') { e.preventDefault(); setCreateOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(timer); };
  }, [nav]);

  return (
    <div className={`min-h-full flex flex-col ${wideCanvas ? 'atlas-wide-canvas' : ''}`}>
      {/* Top header */}
      <a href="#main-content" className="skip-link">Skip to content</a>
      <header className="app-header sticky top-0 z-30 border-b border-border">
        <div className="app-toolbar">
          <button onClick={() => nav('/')} className="app-brand shrink-0 rounded-lg" aria-label="ATLAS home — Tracx Systems" title="Adaptive Troubleshooting & Live Assistance System — Tracx Systems">
            <AtlasBrand />
          </button>

          {/* Global search (spec §22.1): quick-search dropdown, / and Ctrl+K
              activation, full results at /search. The Tickets page keeps its
              own in-list filter — this one searches everything. */}
          <GlobalSearch />
          <MyCourtBadge />

          <button className="btn-brand" onClick={() => setCreateOpen(true)} title="Create ticket (C)"><Icon name="create" /> Create</button>
          {features.data?.flags.morningBrief && <MorningBriefWidget />}
          <NotificationBell />
          <button className="btn-icon" onClick={toggleTheme} title="Toggle theme" aria-label="Toggle theme"><Icon name="theme" /></button>
          {devMode ? (
            <>
              <Select
                size="sm"
                className="!py-1 text-xs max-w-[150px]"
                value={user}
                title="Switch persona (dev session)"
                aria-label="Switch persona (dev session)"
                onChange={(v) => { setActiveUser(v); setUser(v); qc.clear(); }}
                options={PERSONAS.map((p) => ({ value: p, label: p.split('@')[0] }))}
              />
              <button className="btn-icon" onClick={() => nav('/profile')} title="My profile" aria-label="My profile"><Icon name="user" /></button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <button className="flex items-center gap-2 rounded-lg" onClick={() => nav('/profile')} title="My profile" aria-label="My profile">
                {me?.user && <UserAvatar id={me.user.id} name={me.user.name || me.user.email} size={30} />}
                <span className="text-xs text-ink hidden lg:flex flex-col items-start max-w-40 truncate" title={me?.user.email}>
                  {me?.user.name ?? me?.user.email}
                  {me?.user.role ? <span className="text-[10px] text-muted">{roleLabel(me.user.role)}</span> : null}
                </span>
              </button>
              <button className="btn-ghost text-xs" onClick={() => void signOut()} title="Sign out">
                Sign out
              </button>
            </div>
          )}
        </div>

        {/* Primary nav — direct links + dropdown groups (#11) */}
        <nav className="app-navigation" aria-label="Primary navigation">
          {NAV_GROUPS.map((entry) => {
            if (!isGroup(entry)) {
              return (
                <NavLink
                  key={entry.path}
                  to={entry.path}
                  end={entry.end}
                  className={({ isActive }) =>
                    `app-nav-link ${
                      isActive ? 'app-nav-link-active' : 'app-nav-link-idle'
                    }`
                  }
                >
                  {entry.label}
                </NavLink>
              );
            }
            let groupItems: NavItem[] = entry.items.filter((i) => available(i.path) && i.hideIf?.({ showRoadmap }) !== true && (!i.roles || i.roles.includes(role))).map(i => ({ ...i, wip: false }));
            if (entry.label === 'Tickets' && myViewItems.length > 0) groupItems = [...groupItems, ...myViewItems];
            if (groupItems.length === 0) return null;
            const active = groupItems.some((i) => pathActive(pathname, i.path.split('?')[0]!, i.end));
            return <NavGroup key={entry.label} label={entry.label} items={groupItems} active={active} pathname={pathname} />;
          })}
        </nav>
      </header>

      {/* Workspace tabs (Product/Support/Engineering/DevOps) only under the
          ticket surfaces (All Tickets + boards/workspaces) — not on every page. */}
      {(pathname === '/tickets' || pathname.startsWith('/tickets/') || pathname.startsWith('/board') || pathname.startsWith('/workspaces')) && <WorkspaceSwitcher />}
      <main id="main-content" tabIndex={-1} className="app-main flex-1 min-w-0 px-4 sm:px-6 py-6 sm:py-7 w-full outline-none"><FeatureGate>{children}</FeatureGate></main>

      {/* Footer: build/version stamp. Format MAJOR.MINOR.REVISION(YYMMDD).BUILD. */}
      <footer className="app-footer px-4 sm:px-6 py-4 border-t border-border flex items-center justify-between gap-4 text-xs text-muted">
        <span className="flex items-center gap-3">
          <span className="dark:hidden"><TracxBrand /></span>
          <span className="hidden dark:inline"><TracxBrand light /></span>
          <span className="border-l border-border pl-3">ATLAS</span>
        </span>
        <span
          className="font-mono"
          title="version · major.minor.revision(YYMMDD build date).build(deploy # that day)"
        >
          v{APP_VERSION}
        </span>
      </footer>

      {createOpen && (
        <Suspense fallback={null}>
          <CreateTicketModal initialType={pathname === '/board/infra' || new URLSearchParams(search).get('department') === 'devops' ? 'infrastructure' : ['product', 'engineering'].includes(new URLSearchParams(search).get('department') ?? '') ? 'feature_request' : undefined} open onClose={() => setCreateOpen(false)} />
        </Suspense>
      )}
      <NotificationToaster />
      <Toaster />
      <ImageLightbox />
    </div>
  );
}

/**
 * A top-level nav dropdown group (#11). Closes on outside click + route change.
 *
 * The menu is portaled to <body> and positioned with fixed coords from the
 * trigger's rect: the nav bar sets `overflow-x-auto` (horizontal scroll on
 * narrow screens), and per the CSS overflow spec that forces overflow-y to
 * compute as `auto` too — which would clip an in-flow absolute dropdown. The
 * portal escapes that clip entirely.
 */
function NavGroup({ label, items, active, pathname }: { label: string; items: NavItem[]; active: boolean; pathname: string }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const { search } = useLocation();
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => { setOpen(false); }, [pathname, search]);
  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); btnRef.current?.focus(); }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open]);
  // Anchor the portaled menu under the trigger, and keep it aligned if the
  // window scrolls or resizes while open.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ left: Math.max(8, Math.min(r.left, window.innerWidth - 248)), top: r.bottom + 6 });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);
  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOpen(true);
            const last = e.key === 'ArrowUp';
            requestAnimationFrame(() => {
              const links = menuRef.current?.querySelectorAll<HTMLAnchorElement>('a');
              links?.[last ? links.length - 1 : 0]?.focus();
            });
          }
        }}
        className={`app-nav-link ${
          active ? 'app-nav-link-active' : 'app-nav-link-idle'
        }`}
      >
        {label} <Icon name={open ? 'chevron-up' : 'chevron-down'} />
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div ref={menuRef} id={menuId} aria-label={label} data-atlas-popover className="nav-popover" style={{ left: pos.left, top: pos.top, maxHeight: `calc(100dvh - ${pos.top + 12}px)` }}
            onKeyDown={(e) => {
              const links = Array.from(menuRef.current?.querySelectorAll<HTMLAnchorElement>('a') ?? []);
              const index = links.indexOf(document.activeElement as HTMLAnchorElement);
              let next = index;
              if (e.key === 'ArrowDown') next = (index + 1) % links.length;
              else if (e.key === 'ArrowUp') next = (index - 1 + links.length) % links.length;
              else if (e.key === 'Home') next = 0;
              else if (e.key === 'End') next = links.length - 1;
              else return;
              e.preventDefault(); links[next]?.focus();
            }}>
            {items.map((i) => (
              <Fragment key={i.path}>
                {i.heading && (
                  <div className="px-3 pt-2 pb-1 mt-1 border-t border-border text-[10px] uppercase tracking-wide text-muted">{i.heading}</div>
                )}
                {i.wip ? (
                  <div
                    className="flex items-center justify-between px-3 py-1.5 text-sm text-muted/60 cursor-not-allowed select-none"
                    title="In progress — coming soon"
                  >
                    <span>{i.label}</span>
                    <span className="text-[10px] uppercase tracking-wide rounded-full bg-surface2 text-muted px-1.5 py-0.5">In progress</span>
                  </div>
                ) : (
                  <NavLink
                    to={i.path}
                    end={i.end}
                    onClick={() => setOpen(false)}
                    className={() => `nav-popover-link ${pathname + search === i.path ? 'bg-brand/10 text-brand font-medium' : 'text-ink'}`}
                  >
                    {i.label}
                    {i.shortcut && <span className="text-[10px] text-muted font-normal" aria-hidden="true">G {i.shortcut.toUpperCase()}</span>}
                  </NavLink>
                )}
              </Fragment>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

/**
 * Raises a transient toast for newly-arrived high/critical notifications (§17.2).
 * Driven by the current user's unread feed, which the SSE handler invalidates on
 * `notification.created` — so it's both live and privacy-safe (the per-user API
 * filters recipients; we never toast another user's notification). Low/medium
 * priority updates are intentionally silent to avoid toast spam.
 */
function NotificationToaster() {
  const seen = useRef<Set<string> | null>(null);
  const { data } = useNotifications({ status: 'unread', limit: 8 }, { refetchInterval: 60_000 });

  useEffect(() => {
    if (!data) return;
    // First load seeds the seen-set silently so we don't toast history on mount.
    if (seen.current === null) {
      seen.current = new Set(data.items.map((n) => n.id));
      return;
    }
    for (const n of data.items) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (n.priority === 'high' || n.priority === 'critical') {
        toast(n.title, n.priority === 'critical' ? 'error' : 'info');
      }
    }
  }, [data]);

  return null;
}

function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); bellRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const [tab, setTab] = useState<CategoryFilter>('ALL');
  const { data: counts } = useNotificationCounts();
  const { data, isLoading, isError, refetch } = useNotifications(
    { category: tab, limit: 12 },
    { enabled: open, refetchInterval: open ? 30_000 : undefined },
  );
  const { markRead, markAllRead, markTicketRead } = useNotificationActions();

  const unread = counts?.totalUnread ?? 0;
  const items = data?.items ?? [];

  function markTicketReadFn(n: Notif) {
    if (!n.ticketId) return;
    markTicketRead.mutate(n.ticketId, {
      onSuccess: (res) => toast(
        res.count > 0
          ? `Marked ${res.count} notification${res.count === 1 ? '' : 's'} for ${n.ticketCode ?? 'this ticket'} as read`
          : `No unread notifications for ${n.ticketCode ?? 'this ticket'}`,
      ),
    });
  }

  const tabCount = (key: string): number | undefined =>
    key === 'ALL' ? counts?.all
      : key === 'MENTION' ? counts?.mentions
        : key === 'ASSIGNED' ? counts?.assigned
          : key === 'WATCHING' ? counts?.watching
            : undefined;

  function openNotif(n: Notif) {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    const href = notifHref(n);
    if (href) nav(href);
  }

  return (
    <div className="relative">
      <button ref={bellRef} className="btn-icon relative" onClick={() => setOpen((o) => !o)} title="Notifications" aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} aria-expanded={open} aria-controls={open ? 'notification-panel' : undefined}>
        <Icon name="bell" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] leading-none rounded-full px-1 py-0.5 min-w-[16px] text-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div id="notification-panel" aria-label="Notifications" className="notification-popover absolute right-0 mt-2 w-[380px] card shadow-pop z-50 flex flex-col max-h-[500px]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <span className="font-semibold text-sm">Notifications</span>
              {unread > 0 && (
                <button className="text-xs text-brand hover:underline" onClick={() => markAllRead.mutate(tab)}>
                  Mark all read
                </button>
              )}
            </div>

            {/* Quick tabs */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border overflow-x-auto">
              {DROPDOWN_TABS.map((t) => {
                const c = tabCount(t.key);
                const active = tab === t.category;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.category!)}
                    className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition-colors ${
                      active ? 'bg-brand text-brand-ink' : 'text-muted hover:bg-surface2'
                    }`}
                  >
                    {t.label}
                    {c ? <span className={`ml-1 ${active ? '' : 'text-brand'}`}>{c}</span> : null}
                  </button>
                );
              })}
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1">
              {isLoading ? (
                <div className="p-3 space-y-3">
                  {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
                </div>
              ) : isError ? (
                <div className="p-6 text-center text-sm text-muted">
                  <div className="mb-2">Unable to load notifications.</div>
                  <button className="btn-ghost text-xs" onClick={() => refetch()}>Try again</button>
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-sm">
                  <div className="text-3xl opacity-40 mb-2"><Icon name="bell" /></div>
                  <div className="font-medium text-ink">{EMPTY_COPY[tab]?.title ?? "You're all caught up"}</div>
                  <div className="text-xs text-muted mt-0.5">{EMPTY_COPY[tab]?.hint}</div>
                </div>
              ) : (
                items.map((n) => (
                  <NotificationItem
                    key={n.id}
                    n={n}
                    variant="dropdown"
                    onOpen={openNotif}
                    onToggleRead={(x) => !x.read && markRead.mutate(x.id)}
                    onMarkTicketRead={markTicketReadFn}
                  />
                ))
              )}
            </div>

            <button
              className="px-3 py-2.5 text-sm text-brand hover:bg-surface2 border-t border-border text-center font-medium"
              onClick={() => { setOpen(false); nav('/notifications'); }}
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}
